'use client';

import { useCommendaOSEntitySchema } from '@commenda/api/commenda-os-dsl';
import {
  Badge,
  Label,
  LoadingIndicator,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@commenda/kakashi';
import { useEffect, useMemo } from 'react';
import { COMMENDA_OS_DATA_SOURCE } from '../../constants';
import type { DataSourceConfig } from '@commenda/types';

export function DirectorDocumentEditor({
  props,
  dataSource,
  onUpdateProps,
  onDataSourceChange,
}: {
  props: Record<string, unknown>;
  dataSource?: DataSourceConfig;
  onUpdateProps: (key: string, value: unknown) => void;
  onDataSourceChange: (dataSource: DataSourceConfig | undefined) => void;
}) {
  const { entitySchema, isLoading } =
    useCommendaOSEntitySchema('directorDocument');

  const directorDocOptions = useMemo(() => {
    if (!entitySchema?.filters?.documentKind?.enumOptions) {
      return [
        { value: 'governmentId', label: 'Government ID' },
        { value: 'panCard', label: 'PAN Card' },
        { value: 'aadharCard', label: 'Aadhar Card' },
        { value: 'addressProof', label: 'Address Proof' },
        { value: 'dscProof', label: 'DSC Proof' },
        { value: 'bankTransferProof', label: 'Bank Transfer Proof' },
      ];
    }
    return entitySchema.filters.documentKind.enumOptions.map(
      (opt: { value: string; label: string }) => ({
        value: opt.value,
        label: opt.label,
      })
    );
  }, [entitySchema]);

  const relationshipDocOptions = useMemo(
    () => [
      { value: 'shareCertificate', label: 'Share Certificate' },
      { value: 'boardResolution', label: 'Board Resolution' },
      {
        value: 'sharePurchaseAndSaleAgreement',
        label: 'Share Purchase & Sale Agreement',
      },
    ],
    []
  );

  const documentKindOptions = useMemo(
    () => [...directorDocOptions, ...relationshipDocOptions],
    [directorDocOptions, relationshipDocOptions]
  );

  const selectedDocumentKind = (props?.documentKind as string) || '';
  const mode = (props?.mode as 'read' | 'read-write') || 'read-write';

  useEffect(() => {
    if (!dataSource || dataSource.type !== COMMENDA_OS_DATA_SOURCE) {
      onDataSourceChange({
        type: COMMENDA_OS_DATA_SOURCE,
        commendaOS: {
          entity: 'directorDocument',
          contextBindings: [],
        },
      });
    }
  }, [dataSource, onDataSourceChange]);

  const selectedKindMeta = documentKindOptions.find(
    (opt: { value: string }) => opt.value === selectedDocumentKind
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <h3 className="txt-small-medium text-text-secondary">
          Director Document Configuration
        </h3>
        <Badge variant="gray">Commenda OS</Badge>
      </div>

      <div className="space-y-4 pl-4 border-l-2 border-border-secondary">
        <div className="space-y-2">
          <Label>Document Type</Label>
          {isLoading ? (
            <div className="flex items-center gap-2 p-2">
              <LoadingIndicator size={16} />
              <span className="txt-small-regular text-text-tertiary">
                Loading document types...
              </span>
            </div>
          ) : (
            <Select
              value={selectedDocumentKind}
              onValueChange={(value) => onUpdateProps('documentKind', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select document type..." />
              </SelectTrigger>
              <SelectContent>
                {directorDocOptions.length > 0 && (
                  <>
                    {directorDocOptions.map(
                      (opt: { value: string; label: string }) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      )
                    )}
                  </>
                )}
                {relationshipDocOptions.length > 0 && (
                  <>
                    {relationshipDocOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </>
                )}
              </SelectContent>
            </Select>
          )}
          {selectedKindMeta && (
            <p className="txt-xs-regular text-text-tertiary">
              Selected: {selectedKindMeta.label}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label>Mode</Label>
          <Select
            value={mode}
            onValueChange={(value) => onUpdateProps('mode', value)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="read">Read Only</SelectItem>
              <SelectItem value="read-write">Read &amp; Write</SelectItem>
            </SelectContent>
          </Select>
          <p className="txt-xs-regular text-text-tertiary">
            Read Only: Display document status only. Read &amp; Write: Allow
            uploading or replacing the document.
          </p>
        </div>
      </div>
    </div>
  );
}
